import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Car, Search, Star, Zap, Gauge, Users, Weight, CircleCheck, ExternalLink } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import AdSlot from '../components/AdSlot';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import ShareButtons from '../components/ShareButtons';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function StarRating({ value, onChange, readonly }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(s => (
        <button
          key={s}
          onClick={() => !readonly && onChange && onChange(s)}
          className={`${readonly ? 'cursor-default' : 'cursor-pointer'} transition-colors`}
          type="button"
        >
          <Star className={`w-4 h-4 ${s <= value ? 'fill-yellow-400 text-yellow-400' : 'text-slate-200'}`} />
        </button>
      ))}
    </div>
  );
}

function VehicleCard({ vehicle, onClick }) {
  const imgSrc = vehicle.image_url
    ? (vehicle.image_url.startsWith('http') ? vehicle.image_url : `${API}/files/${vehicle.image_url}`)
    : null;

  return (
    <div
      className="card-hover cursor-pointer group overflow-hidden"
      onClick={() => onClick(vehicle)}
      data-testid={`vehicle-card-${vehicle._id}`}
    >
      <div className="aspect-video bg-slate-100 relative overflow-hidden rounded-t-2xl -mx-0 -mt-0">
        {imgSrc ? (
          <img src={imgSrc} alt={vehicle.model} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Car className="w-16 h-16 text-slate-200" />
          </div>
        )}
        <div className="absolute top-3 left-3">
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full shadow-sm ${
            vehicle.category === 'L6e' ? 'bg-emerald-500 text-white' : 'bg-cyan-500 text-white'
          }`}>{vehicle.category}</span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-bold text-slate-800 text-lg">{vehicle.brand} {vehicle.model}</h3>
        {vehicle.year && <p className="text-sm text-slate-400">{vehicle.year}</p>}
        <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-slate-600">
          {vehicle.max_speed > 0 && (
            <div className="flex items-center gap-1.5 bg-slate-50 rounded-lg px-2 py-1.5">
              <Gauge className="w-3.5 h-3.5 text-emerald-500" />
              <span>Max {vehicle.max_speed} km/s</span>
            </div>
          )}
          {vehicle.range_km > 0 && (
            <div className="flex items-center gap-1.5 bg-slate-50 rounded-lg px-2 py-1.5">
              <Zap className="w-3.5 h-3.5 text-emerald-500" />
              <span>{vehicle.range_km} km menzil</span>
            </div>
          )}
          {vehicle.seats > 0 && (
            <div className="flex items-center gap-1.5 bg-slate-50 rounded-lg px-2 py-1.5">
              <Users className="w-3.5 h-3.5 text-emerald-500" />
              <span>{vehicle.seats} kişilik</span>
            </div>
          )}
          {vehicle.license_required && (
            <div className="flex items-center gap-1.5 bg-slate-50 rounded-lg px-2 py-1.5">
              <CircleCheck className="w-3.5 h-3.5 text-emerald-500" />
              <span>{vehicle.license_required}</span>
            </div>
          )}
        </div>
        {vehicle.rating_count > 0 && (
          <div className="flex items-center gap-2 mt-3">
            <StarRating value={Math.round(vehicle.rating_avg)} readonly />
            <span className="text-xs text-slate-400">{vehicle.rating_avg} ({vehicle.rating_count} oy)</span>
          </div>
        )}
        <Link
          to={`/arac/${vehicle._id}`}
          onClick={e => e.stopPropagation()}
          className="inline-flex items-center text-xs text-emerald-700 hover:text-emerald-800 font-medium mt-3"
          data-testid={`vehicle-detail-link-${vehicle._id}`}
        >
          Detay sayfası <ExternalLink className="w-3 h-3 ml-1" />
        </Link>
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap" onClick={e => e.stopPropagation()}>
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Paylaş</span>
          <ShareButtons
            size="xs"
            url={`${window.location.origin}/arac/${vehicle._id}`}
            title={`${vehicle.brand} ${vehicle.model}`}
            text={`${vehicle.brand} ${vehicle.model} · MiniOto katalog`}
          />
        </div>
      </div>
    </div>
  );
}

export default function CatalogPage() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [myRating, setMyRating] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchVehicles = async (category = '') => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/vehicles${category ? `?category=${category}` : ''}`);
      setVehicles(r.data.vehicles || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVehicles(); }, []);

  // Dialog açıldığında kullanıcının mevcut oyunu getir (oyunu değiştirebilmesi için)
  useEffect(() => {
    if (!selectedVehicle) { setMyRating(0); return; }
    if (!user) { setMyRating(0); return; }
    axios.get(`${API}/vehicles/${selectedVehicle._id}/my-rating`, { withCredentials: true })
      .then(r => setMyRating(r.data.rating || 0))
      .catch(() => setMyRating(0));
  }, [selectedVehicle, user]);

  const filtered = vehicles.filter(v =>
    !search || `${v.brand} ${v.model}`.toLowerCase().includes(search.toLowerCase())
  );

  const l6eVehicles = filtered.filter(v => v.category === 'L6e');
  const l7eVehicles = filtered.filter(v => v.category === 'L7e');

  const handleRate = async (rating) => {
    if (!user) {
      toast.error('Oy vermek için giriş yapmalısınız');
      navigate('/giris');
      return;
    }
    try {
      await axios.post(`${API}/vehicles/${selectedVehicle._id}/rate`, { rating }, { withCredentials: true });
      toast.success(myRating ? 'Oyunuz güncellendi!' : 'Oyunuz kaydedildi!');
      setMyRating(rating);
      const r = await axios.get(`${API}/vehicles/${selectedVehicle._id}`);
      setSelectedVehicle(r.data.vehicle);
    } catch {
      toast.error('Değerlendirme kaydedilemedi');
    }
  };

  const imgSrc = (url) => url ? (url.startsWith('http') ? url : `${API}/files/${url}`) : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO
        title="Araç Kataloğu · L6e ve L7e Modeller"
        description="L6e ve L7e kategorisindeki hafif dört tekerlekli araç modellerini keşfet: teknik özellikler, kullanıcı puanları ve karşılaştırmalar."
      />
      <Breadcrumbs items={[{ name: 'Araçlar' }]} />
      <AdSlot placement="catalog_top" className="mb-6" />
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Araç Kataloğu</h1>
        <p className="text-slate-500 mt-1">L6e ve L7e kategorisindeki araçları keşfedin</p>
      </div>

      {/* Search */}
      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Marka veya model ara..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-10"
          data-testid="catalog-search-input"
        />
      </div>

      <Tabs defaultValue="all" data-testid="catalog-tabs">
        <TabsList className="mb-6">
          <TabsTrigger value="all" data-testid="catalog-tab-all">Tümü ({filtered.length})</TabsTrigger>
          <TabsTrigger value="l6e" data-testid="catalog-tab-l6e" className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700">
            L6e ({l6eVehicles.length})
          </TabsTrigger>
          <TabsTrigger value="l7e" data-testid="catalog-tab-l7e" className="data-[state=active]:bg-cyan-50 data-[state=active]:text-cyan-700">
            L7e ({l7eVehicles.length})
          </TabsTrigger>
        </TabsList>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-10 h-10 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <TabsContent value="all">
              {filtered.length === 0 ? (
                <div className="text-center py-16">
                  <Car className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                  <p className="text-slate-500">Araç bulunamadı</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {filtered.map(v => <VehicleCard key={v._id} vehicle={v} onClick={setSelectedVehicle} />)}
                </div>
              )}
            </TabsContent>
            <TabsContent value="l6e">
              <div className="mb-4 p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                <p className="text-emerald-700 text-sm font-medium">L6e: Hafif dört tekerlekli · Max 45 km/s · AM / B1 Ehliyet · 16+ Yaş</p>
              </div>
              {l6eVehicles.length === 0 ? (
                <div className="text-center py-12"><Car className="w-12 h-12 text-slate-200 mx-auto mb-3" /><p className="text-slate-500">L6e araç bulunamadı</p></div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {l6eVehicles.map(v => <VehicleCard key={v._id} vehicle={v} onClick={setSelectedVehicle} />)}
                </div>
              )}
            </TabsContent>
            <TabsContent value="l7e">
              <div className="mb-4 p-3 bg-cyan-50 rounded-xl border border-cyan-100">
                <p className="text-cyan-700 text-sm font-medium">L7e: Ağır dört tekerlekli · Max 90 km/s · B Ehliyet · 18+ Yaş</p>
              </div>
              {l7eVehicles.length === 0 ? (
                <div className="text-center py-12"><Car className="w-12 h-12 text-slate-200 mx-auto mb-3" /><p className="text-slate-500">L7e araç bulunamadı</p></div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {l7eVehicles.map(v => <VehicleCard key={v._id} vehicle={v} onClick={setSelectedVehicle} />)}
                </div>
              )}
            </TabsContent>
          </>
        )}
      </Tabs>

      {/* Vehicle detail dialog */}
      <Dialog open={!!selectedVehicle} onOpenChange={() => setSelectedVehicle(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedVehicle && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-extrabold">{selectedVehicle.brand} {selectedVehicle.model}</DialogTitle>
              </DialogHeader>
              {selectedVehicle.image_url && (
                <img src={imgSrc(selectedVehicle.image_url)} alt={selectedVehicle.model} className="w-full h-48 object-cover rounded-xl" />
              )}
              <div className="grid grid-cols-2 gap-3 mt-2">
                <span className={`col-span-2 text-sm font-bold px-3 py-1 rounded-full w-fit ${selectedVehicle.category === 'L6e' ? 'bg-emerald-100 text-emerald-700' : 'bg-cyan-100 text-cyan-700'}`}>
                  {selectedVehicle.category}
                </span>
                {selectedVehicle.year && <div className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-500">Model Yılı</div><div className="font-semibold">{selectedVehicle.year}</div></div>}
                {selectedVehicle.max_speed > 0 && <div className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-500">Maks Hız</div><div className="font-semibold">{selectedVehicle.max_speed} km/s</div></div>}
                {selectedVehicle.range_km > 0 && <div className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-500">Menzil</div><div className="font-semibold">{selectedVehicle.range_km} km</div></div>}
                {selectedVehicle.battery_kwh > 0 && <div className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-500">Batarya</div><div className="font-semibold">{selectedVehicle.battery_kwh} kWh</div></div>}
                {selectedVehicle.power_kw > 0 && <div className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-500">Motor Gücü</div><div className="font-semibold">{selectedVehicle.power_kw} kW</div></div>}
                {selectedVehicle.weight_kg > 0 && <div className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-500">Ağırlık</div><div className="font-semibold">{selectedVehicle.weight_kg} kg</div></div>}
                {selectedVehicle.seats > 0 && <div className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-500">Koltuk</div><div className="font-semibold">{selectedVehicle.seats} kişi</div></div>}
                {selectedVehicle.license_required && <div className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-500">Ehliyet</div><div className="font-semibold">{selectedVehicle.license_required}</div></div>}
                {(selectedVehicle.price_min > 0 || selectedVehicle.price_max > 0) && (
                  <div className="col-span-2 bg-slate-50 rounded-xl p-3">
                    <div className="text-xs text-slate-500">Fiyat Aralığı</div>
                    <div className="font-semibold">
                      {selectedVehicle.price_min > 0 ? `₺${selectedVehicle.price_min.toLocaleString('tr-TR')}` : ''}
                      {selectedVehicle.price_min > 0 && selectedVehicle.price_max > 0 ? ' - ' : ''}
                      {selectedVehicle.price_max > 0 ? `₺${selectedVehicle.price_max.toLocaleString('tr-TR')}` : ''}
                    </div>
                  </div>
                )}
              </div>
              {selectedVehicle.description && (
                <p className="text-slate-600 text-sm leading-relaxed mt-2">{selectedVehicle.description}</p>
              )}
              <div className="border-t border-slate-100 pt-4 mt-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">Değerlendirme</p>
                    <div className="flex items-center gap-2">
                      <StarRating value={Math.round(selectedVehicle.rating_avg)} readonly />
                      <span className="text-sm text-slate-500">{selectedVehicle.rating_avg || 0} ({selectedVehicle.rating_count || 0} oy)</span>
                    </div>
                  </div>
                  {user ? (
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Puanınız: <span className="text-slate-400">{myRating ? '(oyunuzu istediğiniz zaman değiştirebilirsiniz)' : ''}</span></p>
                      <StarRating value={myRating} onChange={handleRate} />
                    </div>
                  ) : (
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Oy vermek için</p>
                      <Link to="/giris" className="inline-flex items-center text-xs font-semibold text-emerald-700 hover:text-emerald-800" data-testid="catalog-login-to-vote">
                        Giriş yap <ExternalLink className="w-3 h-3 ml-1" />
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
